import {
  type SearchEndpointArgs,
  useGetDemoQuery,
  useInfiniteScroll,
  useLazyGetModificationsCountQuery,
  useLazyGetSearchResultsQuery,
} from '@carboncopyco/data';
import {
  GroupSchemaType,
  NavigationEditSchemaType,
  RecordingTypes,
} from '@carboncopyco/types';
import type { SchemaType, Sort } from '@carboncopyco/types';
import constate from 'constate';
import { useEffect, useMemo, useState } from 'react';
import type { CategorizedData, DemoSearchResult } from './types';
import { categorizeData, sumSelectedModifications } from './utils';

type ImportManagerContextProps = {
  organizationId: string;
  workspaceId: string;
  draftId: string;
  currentDemoId: string;
};

const useImportManagerContext = ({
  workspaceId,
  organizationId,
  draftId,
  currentDemoId,
}: ImportManagerContextProps) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('last_updated');
  const [creators, setCreators] = useState<Record<string, boolean>>({});
  const [selectedDemoId, setSelectedDemoId] = useState<number | null>(null);
  const [selectedSchemas, setSelectedSchemas] = useState<SchemaType[]>([]);
  const [totalSelectedModifications, setTotalSelectedModifications] =
    useState(0);
  const [categorizedDataModifications, setCategorizedDataModifications] =
    useState<CategorizedData[]>();
  const [indeterminateSchemas, setIndeterminateSchemas] = useState<
    SchemaType[]
  >([]);

  const {
    fetchFirstBatch,
    isEmpty,
    isLoadingBatch,
    isLoading,
    fetchNextBatch,
    items,
    error,
  } = useInfiniteScroll<DemoSearchResult>(useLazyGetSearchResultsQuery, {
    preferCacheValue: false,
  });

  const [
    getModificationsCount,
    { data: modifications, isFetching: isFetchingModifications },
  ] = useLazyGetModificationsCountQuery();

  const { data: demo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: currentDemoId,
  });

  const fetchArgs = useMemo<SearchEndpointArgs>(() => {
    const creatorsArray = Object.entries(creators)
      .filter(([, bool]) => bool)
      .map(([creator]) => creator) as string[];
    return {
      organizationId: String(organizationId),
      query,
      workspaces: workspaceId,
      entities: ['demo'],
      creators: creatorsArray.length ? creatorsArray : undefined,
      sort,
    };
  }, [organizationId, query, workspaceId, creators, sort]);

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchArgs, fetchFirstBatch]);

  useEffect(() => {
    if (selectedDemoId) {
      getModificationsCount({
        organizationId,
        entityId: selectedDemoId,
      });
    }
  }, [selectedDemoId, organizationId, getModificationsCount]);

  const setSchemas = (data: CategorizedData[]) => {
    const isBlockSettingsExist = modifications?.counts.some(
      ({ schemaType }) =>
        schemaType === NavigationEditSchemaType.blockInteractionsSettings
    );
    const schemas = data?.flatMap(({ schemaTypes }) =>
      schemaTypes.filter((schemaType) =>
        demo?.recordingType === RecordingTypes.overlay
          ? schemaType !== 'edit'
          : true
      )
    );
    if (isBlockSettingsExist) {
      schemas.push(NavigationEditSchemaType.blockInteractionsSettings);
    }
    setSelectedSchemas(schemas as SchemaType[]);
    setIndeterminateSchemas([]);
    setTotalSelectedModifications(
      sumSelectedModifications(data, demo?.recordingType)
    );
  };

  useEffect(() => {
    if (!modifications?.counts) {
      return;
    }
    const data = categorizeData(modifications.counts);
    setCategorizedDataModifications(data);
    setTotalSelectedModifications(0);
    setSchemas(data);
  }, [modifications?.counts, demo?.recordingType, selectedDemoId]);

  const isGroupTypeSelected = useMemo(
    () => selectedSchemas.includes(GroupSchemaType.aiPromptGroup),
    [selectedSchemas]
  );

  useEffect(() => {
    if (!modifications?.counts.length) {
      return;
    }
    const filteredData = modifications.counts
      .filter(({ schemaType }) => {
        return (
          !isGroupTypeSelected || !indeterminateSchemas?.includes(schemaType)
        );
      })
      .map((item) => {
        if (
          !isGroupTypeSelected &&
          item.schemaType === GroupSchemaType.aiPromptGroup
        ) {
          return {
            modificationType: item.modificationType,
            schemaType: item.schemaType,
            total: item.total,
          };
        }
        return item;
      });
    const data = categorizeData(filteredData);
    setCategorizedDataModifications(data);
    const selectedData = data.filter((item) => {
      return item.schemaTypes.every((schemaType) =>
        selectedSchemas.includes(schemaType)
      );
    });
    setTotalSelectedModifications(
      sumSelectedModifications(selectedData, demo?.recordingType)
    );
  }, [selectedSchemas, indeterminateSchemas]);

  const resetFilters = () => {
    setCreators({});
    setQuery('');
  };

  return {
    items,
    fetchNextBatch: () => {
      fetchNextBatch(fetchArgs);
    },
    isLoading,
    isLoadingBatch,
    error,
    query,
    updateQuery: setQuery,
    sort,
    updateSort: setSort,
    creators,
    updateCreators: setCreators,
    resetFilters,
    workspaceId,
    organizationId,
    draftId,
    currentDemoId,
    selectedDemoId,
    updateSelectedDemoId: setSelectedDemoId,
    modificationCategories: categorizedDataModifications,
    selectedSchemas,
    updateSelectedSchemas: setSelectedSchemas,
    isFetchingModifications,
    totalSelectedModifications,
    isEmpty,
    recordingType: demo?.recordingType,
    indeterminateSchemas,
    setIndeterminateSchemas,
    isGroupTypeSelected,
  };
};

export const [ImportManagerProvider, useImportManagerState] = constate(
  useImportManagerContext
);
